import type { ProductMediaItem } from "schemas/generated/product";

import * as React from "react";

import { ACCEPT_FILE_EXTENSIONS } from "@/features/assets/constants";
import { useReploFileDropZone } from "@/features/assets/useFileDropZone";

export type UploadingMediaFile = {
  assetId: string;
  url: string;
  type: "image" | "video";
};

type MediaUploadConfig = {
  acceptFileExtensions?: string[];
  currentMedia: ProductMediaItem[];
  onUploadComplete: (newMediaItem: ProductMediaItem) => void;
};

// Helper function to determine file type from MIME type
const getFileType = (file: File): "image" | "video" => {
  return file.type.startsWith("video/") ? "video" : "image";
};

export function useProductMediaUpload(config: MediaUploadConfig): {
  getRootProps: () => React.HTMLAttributes<HTMLDivElement>;
  getInputProps: () => React.InputHTMLAttributes<HTMLInputElement>;
  uploadingFiles: UploadingMediaFile[];
} {
  const [uploadedFiles, setUploadedFiles] = React.useState<Set<string>>(
    new Set(),
  );

  const acceptExtensions = React.useMemo(() => {
    return config.acceptFileExtensions ?? ACCEPT_FILE_EXTENSIONS;
  }, [config.acceptFileExtensions]);

  const { getRootProps, getInputProps, droppedFilesMap } = useReploFileDropZone(
    {
      onUploadComplete: ({ url, file, id }) => {
        if (!file || !id) {
          return;
        }
        setUploadedFiles((prev) => new Set(prev).add(id));

        const newMediaItem: ProductMediaItem = {
          assetId: id,
          url,
          sortOrder: config.currentMedia.length,
          type: getFileType(file),
        };
        config.onUploadComplete(newMediaItem);
      },
      acceptDropAssetType: acceptExtensions,
      allowClickToUpload: true,
    },
  );

  const uploadingFiles = React.useMemo(() => {
    const entries = Array.from(droppedFilesMap.entries()).filter(
      ([id]) => !uploadedFiles.has(id),
    );

    return entries.map(([id, file]) => ({
      assetId: id,
      url: URL.createObjectURL(file),
      type: getFileType(file),
    }));
  }, [droppedFilesMap, uploadedFiles]);

  return {
    getRootProps,
    getInputProps,
    uploadingFiles,
  };
}
